import { themeTokensToAgGridTheme } from '@/themes/adapters/ag-grid';
import { DESIGN_TOKEN_BASE_ID, type AppTheme, type DesignTokenTheme } from './types';
import type { Preset } from '@primeuix/themes/types';

/**
 * The fallback theme id to use when the theme id is not found.
 */
const FALLBACK_THEME_ID = DESIGN_TOKEN_BASE_ID;

/**
 * Get the theme tokens for a given theme id.
 * @param themeId - The theme id to get the theme tokens for.
 * @returns The theme tokens.
 */
export const getThemeTokens = async (themeId: string): Promise<DesignTokenTheme> => {
  try {
    const theme = await import(`@/themes/source/tokens/${themeId}.json`);
    return theme.default;
  } catch (error) {
    if (themeId === FALLBACK_THEME_ID) {
      throw error;
    }

    console.warn(`Theme tokens not found for "${themeId}", using fallback "${FALLBACK_THEME_ID}"`);
    return getThemeTokens(FALLBACK_THEME_ID);
  }
};

/**
 * Get the primevue preset for a given theme id.
 * @param themeId - The theme id to get the primevue preset for.
 * @returns The primevue preset.
 */
export const getThemePrimeVuePreset = async (themeId: string): Promise<Preset> => {
  try {
    const primevuePreset = await import(`@/themes/generated/primevue/${themeId}/ts/index.ts`);
    return primevuePreset.default;
  } catch (error) {
    if (themeId === FALLBACK_THEME_ID) {
      throw error;
    }

    console.warn(`PrimeVue preset not found for "${themeId}", using fallback "${FALLBACK_THEME_ID}"`);
    return getThemePrimeVuePreset(FALLBACK_THEME_ID);
  }
};

/**
 * Get the app theme for a given theme id.
 * @param themeId - The theme id to get the app theme for.
 * @returns The app theme.
 */
export const getAppTheme = async (themeId: string): Promise<AppTheme> => {
  const themeTokens = await getThemeTokens(themeId);
  const agGridTheme = await themeTokensToAgGridTheme(themeTokens);
  const primevuePreset = await getThemePrimeVuePreset(themeId);
  return { themeTokens, agGridTheme, primevuePreset };
};

/**
 * Get the active app theme.
 * @returns The active app theme.
 */
export const getActiveAppTheme = async (): Promise<AppTheme> => {
  const themeId = import.meta.env.VITE_CLIENT;
  return getAppTheme(themeId);
};
